import { takeLatest, put } from 'redux-saga/effects';
import supervisorSlice from 'store/modules/supervisor';
import uiSlice from 'store/modules/ui';
import user from 'api/graphql/user';
import { createSnack, getUserFriendlyError } from 'common/utils/general';
import i18n from 'i18n';

/**
 * Create supervisor saga
 *
 * @param {*} action
 */
function* create(action) {
  try {
    const snack = createSnack({
      key: 'supervisor-create-notification-success',
      variant: 'success',
      message: i18n.t('supervisor.create', { ns: 'notifications' })
    });

    const { payload } = action;
    yield user.create(payload);
    yield put(supervisorSlice.actions.createSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(supervisorSlice.actions.createError(message));
  }
}

/**
 * Find supervisor saga
 *
 * @param {*} action
 */
function* find(action) {
  try {
    const { fields, id } = action.payload;
    const response = yield user.find({ fields, id });

    const {
      data: { user: currentSupervisor }
    } = response;

    yield put(supervisorSlice.actions.findSuccess(currentSupervisor));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(supervisorSlice.actions.findError(message));
  }
}

/**
 * List supervisors saga
 *
 * @param {*} action
 */
function* list(action) {
  try {
    const {
      payload: { filters, pagination }
    } = action;
    const response = yield user.supervisors({ filters, pagination });
    const {
      data: {
        users: { data, paginatorInfo }
      }
    } = response;

    yield put(supervisorSlice.actions.listSuccess(data));
    yield put(
      supervisorSlice.actions.updatePaginatorInfo({
        hasMorePages: paginatorInfo.hasMorePages,
        lastPage: paginatorInfo.lastPage,
        total: paginatorInfo.total
      })
    );
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(supervisorSlice.actions.listError(message));
  }
}

/**
 * Remove supervisors saga
 *
 * @param {*} action
 */
function* remove(action) {
  try {
    const {
      data: { deleteUsers: removedSupervisors }
    } = yield user.remove(action.payload);

    const snack = createSnack({
      key: 'remove-supervisors-notification-success',
      variant: 'success',
      message: i18n.t('supervisor.delete', { count: removedSupervisors.length, ns: 'notifications' })
    });

    yield put(supervisorSlice.actions.removeSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(supervisorSlice.actions.removeError(message));
  }
}

/**
 * Restore supervisors saga
 *
 * @param {*} action
 */
function* restore(action) {
  try {
    const {
      data: { restoreUsers: restoredSupervisors }
    } = yield user.restore(action.payload);

    const snack = createSnack({
      key: 'restore-supervisors-notification-success',
      variant: 'success',
      message: i18n.t('supervisor.restore', {
        count: restoredSupervisors.length,
        ns: 'notifications'
      })
    });

    yield put(supervisorSlice.actions.restoreSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(supervisorSlice.actions.restoreError(message));
  }
}

/**
 * Search supervisors saga
 *
 * @param {*} action
 */
function* search(action) {
  try {
    const { input, fields } = action.payload;
    const response = yield user.search({ fields, input });

    const {
      data: { searchUser: supervisors }
    } = response;

    const options = supervisors.map((option) => ({ value: option.id, label: option.full_name }));

    yield put(supervisorSlice.actions.searchSuccess(options));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(supervisorSlice.actions.searchError(message));
  }
}

/**
 * Update supervisor saga
 *
 * @param {*} action
 */
function* update(action) {
  try {
    const snack = createSnack({
      key: 'update-supervisor-notification-success',
      variant: 'success',
      message: i18n.t('supervisor.update', { ns: 'notifications' })
    });

    yield user.update(action.payload);

    yield put(supervisorSlice.actions.updateSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(supervisorSlice.actions.updateError(message));
  }
}

/**
 * Watch supervisor
 *
 * @export
 */
export default function* watchSupervisor() {
  yield takeLatest(supervisorSlice.actions.create, create);
  yield takeLatest(supervisorSlice.actions.find, find);
  yield takeLatest(supervisorSlice.actions.list, list);
  yield takeLatest(supervisorSlice.actions.remove, remove);
  yield takeLatest(supervisorSlice.actions.restore, restore);
  yield takeLatest(supervisorSlice.actions.search, search);
  yield takeLatest(supervisorSlice.actions.update, update);
}
